import { SIGN_IN_CODE_DIGITS } from "@bandstand/api/SignIn";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import styled from "styled-components";

import { useStores } from "../../stores/useStores";
import { color } from "../../theme/color";
import { FieldLabel } from "../ui/controls";
import { Dialog } from "../ui/Dialog";
import { TextField } from "../ui/TextField";

/**
 * Signs this device in with an email address: first the address, then the code the share server emails to it. No
 * password needed. A name is asked for along with the code, for whoever signs in for the first time.
 */
export const SignInDialog = observer(function SignInDialog({ onClose }: { onClose(): void }) {
    const { account } = useStores();
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [name, setName] = useState("");
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState<string>();
    const digits = code.replace(/\D/g, "").length;

    async function attempt(work: () => Promise<void>) {
        setBusy(true);
        setProblem(undefined);

        try {
            await work();
        } catch (error) {
            setProblem(error instanceof Error ? error.message : String(error));
        } finally {
            setBusy(false);
        }
    }

    const sendCode = () =>
        attempt(async () => {
            await account.requestCode(email.trim());
            setSent(true);
        });

    const signIn = () =>
        attempt(async () => {
            await account.signIn(email.trim(), code, name);
            onClose();
        });

    return sent ? (
        <Dialog
            title="Type the code"
            submit="Sign in"
            canSubmit={!busy && digits === SIGN_IN_CODE_DIGITS}
            onSubmit={signIn}
            onClose={onClose}
        >
            <Text>We emailed a code to {email.trim()}. It works for 10 minutes, on this device only.</Text>
            <FieldLabel>
                Code
                <TextField
                    autoFocus
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="1234 5678"
                    value={code}
                    onChange={setCode}
                />
            </FieldLabel>
            <FieldLabel>
                Your name, if you&apos;re new
                <TextField autoComplete="name" placeholder="How others in your band see you" value={name} onChange={setName} />
            </FieldLabel>
            {problem && <Problem role="alert">{problem}</Problem>}
            <Again type="button" onClick={() => setSent(false)}>
                Use another address, or send a new code
            </Again>
        </Dialog>
    ) : (
        <Dialog title="Sign in" submit="Send a code" canSubmit={!busy && email.includes("@")} onSubmit={sendCode} onClose={onClose}>
            <Text>
                Sign in with your email address to keep your settings on the share server, for every device you sign in on. We email you a
                code; there&apos;s no password.
            </Text>
            <FieldLabel>
                Email address
                <TextField autoFocus type="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={setEmail} />
            </FieldLabel>
            {problem && <Problem role="alert">{problem}</Problem>}
        </Dialog>
    );
});

const Text = styled.p`
    margin: 0;
    font-size: 15px;
    line-height: 21px;
    color: ${color.textSoft};
`;

const Problem = styled.p`
    margin: 0;
    padding: 10px 12px;
    border-radius: 10px;
    background: ${color.dangerSoft};
    color: ${color.danger};
    font-size: 14px;
`;

const Again = styled.button`
    align-self: flex-start;
    padding: 0;
    border: 0;
    background: none;
    color: ${color.accent};
    font-size: 14px;
    cursor: pointer;
`;
